import React, { useState, useMemo, useCallback, useContext } from 'react';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import { Trash } from 'react-bootstrap-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getRuleDisplayName } from '../../constants/ruleTypeDefinitions';
import { isDashboardPath, navigateToTradeWithSymbol, canOpenTradeWithSymbol } from '../../utils/tradeNavigation';
import { TradingContext } from '../../contexts/TradingContext';
import { getRuleTypeColor } from '../../utils/tradingRulesRuleTypeColors';
import {
  formatViolationActual,
  formatViolationLimit,
  getViolationActualColor,
  getProfitTargetViolationLabel,
} from '../../utils/violationLimitActual';
import useAPI from '../../hooks/useApi';
import useIsMobile from '../../hooks/useIsMobile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Squared soft pill to match Trading Alerts lockout table style. */
const violationTypeBadgeStyle = (accentColor) => ({
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.75rem',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  display: 'inline-block',
  backgroundColor: `${accentColor}20`,
  color: accentColor,
});

/**
 * ViolationsTable - Displays trading rule violations in a paginated table
 * 
 * Features:
 * - Client-side pagination (using existing violations data)
 * - Filter by rule type (can be controlled externally)
 * - Filter by account (ensures only selected account violations are shown)
 * - Minimal info display similar to EnforcementAlertsModal
 * - Columns: Type, Symbol, Timestamp, Limit, Actual
 */
const ViolationsTable = ({
  violations = [],
  accountId = null,
  selectedRuleType: externalSelectedRuleType = null,
  noCardWrapper = false,
  onViolationDeleted,
}) => {
  const { colors } = useTheme();
  const isMobile = useIsMobile(768, 1024);
  const { request } = useAPI();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const navigate = useNavigate();
  const location = useLocation();
  const onDashboard = isDashboardPath(location.pathname);
  const symbolLinksToTrade = onDashboard && canOpenTradeWithSymbol(user, selectedAccount);
  const [currentPage, setCurrentPage] = useState(1);
  const [internalSelectedRuleType, setInternalSelectedRuleType] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const pageSize = 20;
  
  // Use external selectedRuleType if provided, otherwise use internal state
  const selectedRuleType = externalSelectedRuleType !== null ? externalSelectedRuleType : internalSelectedRuleType;

  // Strict account filter: show nothing when no account is selected.
  // Violations that carry a mismatched account_id are dropped even if the
  // backend accidentally returns cross-account rows.
  const filteredByAccount = useMemo(() => {
    if (!accountId) return [];
    return violations.filter(v => !v.account_id || v.account_id === accountId);
  }, [violations, accountId]);


  // Get unique rule types from account-filtered violations
  const availableRuleTypes = useMemo(() => {
    const types = new Set();
    filteredByAccount.forEach(v => {
      if (v.rule_type) {
        types.add(v.rule_type);
      }
    });
    return Array.from(types).sort();
  }, [filteredByAccount]);

  // Filter violations by rule type (using account-filtered violations)
  const filteredViolations = useMemo(() => {
    if (selectedRuleType === 'all') {
      return filteredByAccount;
    }
    return filteredByAccount.filter(v => v.rule_type === selectedRuleType);
  }, [filteredByAccount, selectedRuleType]);

  // Paginate filtered violations
  const paginatedViolations = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredViolations.slice(startIndex, startIndex + pageSize);
  }, [filteredViolations, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredViolations.length / pageSize);

  // Format currency
  const formatCurrency = (value) => {
    if (value == null) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (_) {
      return 'N/A';
    }
  };

  // Format limit value
  const formatLimit = (violation) => formatViolationLimit(violation, formatCurrency);

  // Format actual value
  const formatActual = (violation) => formatViolationActual(violation, formatCurrency);

  const closeDeleteModal = useCallback(() => {
    if (deleting) return;
    setPendingDelete(null);
    setDeleteError(null);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete?.id || !accountId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const url = `${BACKEND_URL}/trading_rules_violations/${pendingDelete.id}?account_id=${encodeURIComponent(accountId)}`;
      const json = await request(url, {}, 'DELETE');
      if (json?.success) {
        const deletedId = pendingDelete.id;
        setPendingDelete(null);
        onViolationDeleted?.(deletedId);
        window.dispatchEvent(new CustomEvent('tradingRules:violationDeleted', { detail: { id: deletedId, accountId } }));
      } else {
        setDeleteError('Failed to delete violation. Please try again.');
      }
    } catch (_) {
      setDeleteError('Failed to delete violation. Please try again.');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, accountId, request, onViolationDeleted]);

  const handleSymbolClick = useCallback((symbol) => {
    const sym = String(symbol || '').trim();
    if (!sym || !symbolLinksToTrade) return;
    navigateToTradeWithSymbol(navigate, sym, user, selectedAccount);
  }, [navigate, selectedAccount, symbolLinksToTrade, user]);

  // Reset to page 1 when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedRuleType]);

  // Update internal state when external prop changes
  React.useEffect(() => {
    if (externalSelectedRuleType !== null) {
      setCurrentPage(1);
    }
  }, [externalSelectedRuleType]);

  if (filteredByAccount.length === 0) {
    return null; // Don't render anything if no violations
  }

  const containerStyle = noCardWrapper ? {} : {
    backgroundColor: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '20px'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      {!noCardWrapper && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{
            fontSize: '0.75rem',
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 500
          }}>
            Trading Rule Violations
          </div>

          {/* Filter - Only show if not controlled externally */}
          {externalSelectedRuleType === null && (
          <Dropdown 
            show={showFilterDropdown} 
            onToggle={(isOpen) => setShowFilterDropdown(isOpen)}
          >
            <Dropdown.Toggle
              as={Button}
              variant="outline-custom"
              style={{
                backgroundColor: colors.bgInput || colors.bgSecondary,
                border: `1px solid ${colors.borderLight || colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                padding: '8px 12px',
                fontSize: '0.875rem',
                fontWeight: 400,
                minWidth: '200px',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span>
                {selectedRuleType === 'all' 
                  ? `All Types (${violations.length})`
                  : `${getRuleDisplayName(selectedRuleType)} (${violations.filter(v => v.rule_type === selectedRuleType).length})`}
              </span>
              <i className="fas fa-caret-down" style={{ marginLeft: '8px', fontSize: '0.75rem' }}></i>
            </Dropdown.Toggle>
            
            <Dropdown.Menu
              align="start"
              style={{
                backgroundColor: colors.bgInput,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: '8px',
                padding: '8px 0',
                marginTop: '4px',
                minWidth: '200px',
                maxWidth: '90vw'
              }}
            >
              <Dropdown.Item
                onClick={() => {
                  setInternalSelectedRuleType('all');
                  setShowFilterDropdown(false);
                }}
                style={{
                  color: selectedRuleType === 'all' ? colors.primary : colors.text,
                  backgroundColor: selectedRuleType === 'all' ? `${colors.primary}1A` : 'transparent',
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: selectedRuleType === 'all' ? '600' : '400'
                }}
                onMouseEnter={(e) => {
                  if (selectedRuleType !== 'all') {
                    e.target.style.backgroundColor = `${colors.primary}0D`;
                    e.target.style.color = colors.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedRuleType !== 'all') {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = colors.text;
                  }
                }}
              >
                All Types ({violations.length})
              </Dropdown.Item>
              {availableRuleTypes.map(ruleType => (
                <Dropdown.Item
                  key={ruleType}
                  onClick={() => {
                    setInternalSelectedRuleType(ruleType);
                    setShowFilterDropdown(false);
                  }}
                  style={{
                    color: selectedRuleType === ruleType ? colors.primary : colors.text,
                    backgroundColor: selectedRuleType === ruleType ? `${colors.primary}1A` : 'transparent',
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: selectedRuleType === ruleType ? '600' : '400'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedRuleType !== ruleType) {
                      e.target.style.backgroundColor = `${colors.primary}0D`;
                      e.target.style.color = colors.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedRuleType !== ruleType) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = colors.text;
                    }
                  }}
                >
                  {getRuleDisplayName(ruleType)} ({violations.filter(v => v.rule_type === ruleType).length})
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          )}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse', tableLayout: 'auto' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap', width: isMobile ? 132 : '1%', verticalAlign: 'bottom' }}>
                {isMobile ? (
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.2 }}>
                    <span>Timestamp</span>
                    <span style={{ fontWeight: 400, fontSize: '0.68rem' }}>Account</span>
                  </span>
                ) : 'Timestamp'}
              </th>
              {!isMobile && (
                <th style={{ padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>Account</th>
              )}
              <th style={{ padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>Type</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>Symbol</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', color: colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>Limit</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', color: colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>Actual</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', color: colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap', width: '1%' }} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {paginatedViolations.map((violation, index) => {
              const ruleType = violation.rule_type;
              const accentColor = getRuleTypeColor(ruleType, colors);

              return (
                <tr
                  key={violation.id || index}
                  style={{
                    borderBottom: index < paginatedViolations.length - 1 ? `1px solid ${colors.border}` : 'none'
                  }}
                >
                  <td style={{ padding: '8px 10px', fontSize: '0.8rem', color: colors.text, whiteSpace: isMobile ? 'normal' : 'nowrap', verticalAlign: 'top', maxWidth: isMobile ? 148 : undefined }}>
                    {isMobile ? (
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.25 }}>
                        <span style={{ whiteSpace: 'nowrap' }}>{formatTimestamp(violation.violation_timestamp)}</span>
                        <span style={{
                          color: colors.textMuted,
                          fontFamily: 'monospace',
                          fontSize: '0.68rem',
                          fontWeight: 500,
                          whiteSpace: 'normal',
                          wordBreak: 'break-all',
                        }}
                        >
                          {violation.account_id || accountId || '—'}
                        </span>
                      </span>
                    ) : formatTimestamp(violation.violation_timestamp)}
                  </td>
                  {!isMobile && (
                    <td style={{ padding: '8px 10px', fontSize: '0.8rem', color: colors.textMuted, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {violation.account_id || accountId || '—'}
                    </td>
                  )}
                  <td style={{ padding: '8px 10px', color: colors.text, whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <span style={violationTypeBadgeStyle(accentColor)}>
                      {ruleType === 'profit_target_per_day'
                        ? getProfitTargetViolationLabel(violation)
                        : getRuleDisplayName(ruleType)}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {violation.symbol ? (
                      symbolLinksToTrade ? (
                        <button
                          type="button"
                          onClick={() => handleSymbolClick(violation.symbol)}
                          title={`Open ${violation.symbol} on trade chart`}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            fontWeight: 600,
                            color: colors.primary,
                            cursor: 'pointer',
                            textDecoration: 'none',
                          }}
                        >
                          {violation.symbol}
                        </button>
                      ) : (
                        <span style={{ color: colors.text }}>{violation.symbol}</span>
                      )
                    ) : (
                      <span style={{ color: colors.text }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: colors.text, whiteSpace: 'nowrap' }}>
                    {formatLimit(violation)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: getViolationActualColor(violation, colors), fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatActual(violation)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {violation.id != null && (
                      <button
                        type="button"
                        aria-label={`Delete ${getRuleDisplayName(ruleType)} violation`}
                        title="Delete violation"
                        onClick={() => {
                          setDeleteError(null);
                          setPendingDelete(violation);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 4,
                          cursor: 'pointer',
                          color: colors.textMuted,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Trash size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal show={pendingDelete != null} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton style={{ backgroundColor: colors.bgCard, borderColor: colors.border, color: colors.text }}>
          <Modal.Title style={{ fontSize: '1rem' }}>Delete violation?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: colors.bgCard, color: colors.text }}>
          <p style={{ marginBottom: 8 }}>Are you really sure?</p>
          <p style={{ marginBottom: 0, color: colors.textMuted, fontSize: '0.9rem' }}>
            This violation will be permanently removed and cannot be recovered.
          </p>
          {pendingDelete && (
            <p style={{ marginTop: 12, marginBottom: 0, fontSize: '0.85rem' }}>
              <strong>{getRuleDisplayName(pendingDelete.rule_type)}</strong>
              {pendingDelete.symbol ? ` · ${pendingDelete.symbol}` : ''}
              {' · '}
              {formatTimestamp(pendingDelete.violation_timestamp)}
            </p>
          )}
          {deleteError && (
            <div style={{ color: colors.danger || '#dc3545', marginTop: 12, fontSize: '0.85rem' }}>{deleteError}</div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: colors.bgCard, borderColor: colors.border }}>
          <Button variant="outline-secondary" onClick={closeDeleteModal} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: `1px solid ${colors.border}`
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: colors.textMuted
          }}>
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredViolations.length)} of {filteredViolations.length}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{
                borderColor: colors.border,
                color: colors.text,
                backgroundColor: 'transparent'
              }}
            >
              Previous
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{
                borderColor: colors.border,
                color: colors.text,
                backgroundColor: 'transparent'
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViolationsTable;
